import { Typography, useTheme } from '@mui/material';
import { Layer, Popup, Source } from '@vis.gl/react-maplibre';
import { Fragment } from 'react';

import { PopupContent } from '@/components/PopupContent';
import { PopupDatePeriod } from '@/components/popup/PopupDatePeriod';
import { PopupTitle } from '@/components/popup/PopupTitle';
import type { TrafficReportFeature } from '@/types/TrafficReportFeature';
import { TrafficReportLayerType } from '@/types/TrafficReportLayerType';
import { TrafficReportType } from '@/types/TrafficReportType';

interface TrafficReportPopupProps {
  feature?: TrafficReportFeature;
  onClose: () => void;
}

export function TrafficReportPopup({ feature, onClose }: TrafficReportPopupProps) {
  const theme = useTheme();

  if (!feature) {
    return null;
  }

  const [longitude, latitude] = feature.geometry.coordinates;

  let title;

  const { type } = feature.properties;

  switch (type) {
    case TrafficReportType.CONSTRUCTION_WORKS:
      title = 'Bauarbeiten';
      break;
    case TrafficReportType.PUBLIC_EVENT:
      title = 'Veranstaltung';
      break;
    case TrafficReportType.ACCIDENT:
      title = 'Unfall';
      break;
    case TrafficReportType.ABNORMAL_TRAFFIC:
      title = 'Stau';
      break;
    case TrafficReportType.REROUTING_MANAGEMENT:
      title = 'Umleitung';
      break;
    case TrafficReportType.MAINTENANCE_WORKS:
      title = 'Wartungsarbeiten';
      break;
    case TrafficReportType.ENVIRONMENTAL_OBSTRUCTION:
      title = 'Naturereignis';
      break;
    case TrafficReportType.CONDITIONS:
      title = 'Umweltalarm';
      break;
    default:
      title = `Sonstiges (${type})`;
  }

  const description = feature.properties.comments?.replace('{}', '').replace('{"', '').replace('"}', '');

  return (
    <Fragment>
      <Popup
        key={feature.properties.id}
        closeButton={false}
        latitude={latitude}
        longitude={longitude}
        maxWidth="min(480px, 75vw)"
        offset={18}
        onClose={onClose}
      >
        <PopupContent onClose={onClose}>
          <PopupTitle>{title}</PopupTitle>

          <PopupDatePeriod feature={feature} />

          {description ? <Typography>{description}</Typography> : null}
        </PopupContent>
      </Popup>

      {feature.properties.originalGeometry ? (
        <Source data={JSON.parse(feature.properties.originalGeometry)} type="geojson">
          <Layer
            beforeId={TrafficReportLayerType.TRAFFIC_REPORT}
            type="line"
            layout={{
              'line-cap': 'round',
              'line-join': 'round',
            }}
            paint={{
              'line-color': theme.palette.error.main,
              'line-width': ['interpolate', ['exponential', 0.5], ['zoom'], 10, 2, 14, 4, 18, 8],
              'line-dasharray': [1, 2],
            }}
          />
        </Source>
      ) : null}
    </Fragment>
  );
}
